import classNames from 'classnames';
import React from 'react';
import Step from '../Step/Step';

import { Colors } from '@oy/uicore';
import styles from './Steppers.module.css';
import { useSteppersImpl } from './useSteppersImpl';

type StepObject = {
  label: string;
  subLabel?: string;
  stepLabel?: string;
  rightIcon?: React.ReactNode;
  isSubStep?: boolean;
};

enum SteppersDirection {
  HORIZONTAL = 'HORIZONTAL',
  VERTICAL = 'VERTICAL',
}

enum StepType {
  NON_LINEAR = 'NON_LINEAR',
  LINEAR = 'LINEAR',
}

enum SteppersVariant {
  STANDARD = 'STANDARD',
  MINIMAL = 'MINIMAL',
}

export type SteppersProps = {
  direction: SteppersDirection;
  activeIndex: number;
  steps: StepObject[];
  highlightAfter?: boolean;
  type?: StepType;
  maxItemsPerRow?: number;
  completedDotColor?: Colors;
  variant?: SteppersVariant;
  containerRef?: React.MutableRefObject<any>;
};

const Steppers = ({
  direction,
  activeIndex,
  steps,
  highlightAfter,
  type,
  maxItemsPerRow,
  completedDotColor,
  variant = SteppersVariant.STANDARD,
  containerRef,
}: SteppersProps) => {
  const isHorizontalDirection = direction === SteppersDirection.HORIZONTAL;
  const isHorizontalLinear = type === StepType.LINEAR;
  const isStandardVariant = variant === SteppersVariant.STANDARD;

  const { translateStepper, stepsIndexRefs } = useSteppersImpl({
    activeIndex,
    containerRef,
  });

  const childFlex =
    maxItemsPerRow !== undefined && isHorizontalDirection
      ? `1 0 calc(${100 / maxItemsPerRow}% - 16px)`
      : undefined;
  const showLine = (index: number) =>
    !maxItemsPerRow || index % maxItemsPerRow !== 0;
  const widthPerItem =
    maxItemsPerRow && steps.length > maxItemsPerRow
      ? `calc(${100 / maxItemsPerRow!}% - 16px)`
      : undefined;

  return (
    <div
      className={classNames(styles.container, {
        [styles.containerHorizontal]:
          isHorizontalDirection && !isHorizontalLinear,
        [styles.containerVertical]:
          !isHorizontalDirection && !isHorizontalLinear,
        [styles.width100]: childFlex,
        [styles.containerLinear]: isHorizontalLinear && isHorizontalDirection,
      })}
      style={{
        flexWrap: childFlex ? 'wrap' : undefined,
        transform: `translateX(-${translateStepper()}px)`,
      }}
    >
      {steps.map(
        ({ label, subLabel, stepLabel, rightIcon, isSubStep }, index) => {
          const isActive = index === activeIndex;
          return (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              className={classNames(styles.stepContainer, {
                [styles.linearWrapper]: isHorizontalLinear,
              })}
              style={{
                flex: childFlex,
                flexDirection: childFlex ? 'row' : undefined,
                maxWidth: widthPerItem,
              }}
              ref={element =>
                ((stepsIndexRefs as any).current[index] = element)
              }
            >
              {index + 1 < steps.length && showLine(index) && (
                <div
                  className={classNames(styles.line, {
                    [styles.lineHorizontal]:
                      isHorizontalDirection && isStandardVariant,
                    [styles.lineHorizontalMinimal]:
                      isHorizontalDirection && !isStandardVariant,
                    [styles.lineVertical]: !isHorizontalDirection,
                    [styles.lineActive]: index < activeIndex,
                    [styles.lineLinear]: isHorizontalLinear,
                  })}
                />
              )}
              {maxItemsPerRow &&
                index + 1 === steps.length &&
                showLine(index) && (
                  <div
                    className={classNames(styles.line, {
                      [styles.lineHorizontal]: isHorizontalDirection,
                      [styles.lineVertical]: !isHorizontalDirection,
                      [styles.lineActive]: index < activeIndex,
                      [styles.lineLinear]: isHorizontalLinear,
                    })}
                  />
                )}
              <Step
                direction={
                  isHorizontalDirection
                    ? Step.Direction.VERTICAL
                    : Step.Direction.HORIZONTAL
                }
                active={isActive}
                completed={index < activeIndex}
                index={index + 1}
                stepLabel={stepLabel}
                label={label}
                subLabel={subLabel}
                rightIcon={rightIcon}
                isSubStep={isSubStep}
                highlightAfter={highlightAfter}
                type={type}
                completedDotColor={completedDotColor}
                variant={
                  isStandardVariant
                    ? Step.Variant.STANDARD
                    : Step.Variant.MINIMAL
                }
              />
            </div>
          );
        },
      )}
    </div>
  );
};

Steppers.Direction = SteppersDirection;
Steppers.Type = StepType;
Steppers.Variant = SteppersVariant;

export default Steppers;
